/**
 * @fileoverview TanStack Query hooks for diagram linking
 * 
 * Hooks for managing diagram links (hierarchical process modeling).
 * 
 * @see Issue #789 - Universal Diagram Linking System
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuth';
import type {
  DiagramLink,
  CreateDiagramLinkInput,
} from '../domains/diagram/models/DiagramLink';

/**
 * Query key factory for diagram link queries
 */
export const diagramLinksKeys = {
  all: ['diagramLinks'] as const,
  byDiagram: (diagramId: string | null) => ['diagramLinks', 'diagram', diagramId] as const,
  byTarget: (diagramId: string | null) => ['diagramLinks', 'target', diagramId] as const,
  byShape: (shapeId: string | null) => ['diagramLinks', 'shape', shapeId] as const,
};

/**
 * Hook to get all links from a source diagram
 */
export function useDiagramLinks(diagramId: string | null) {
  const fetcher = useAuthenticatedFetch();
  const enabled = Boolean(diagramId);

  return useQuery<DiagramLink[]>({
    queryKey: diagramLinksKeys.byDiagram(diagramId),
    enabled,
    queryFn: async (): Promise<DiagramLink[]> => {
      if (!diagramId) return [];
      const r = await fetcher(`/api/diagrams/${diagramId}/diagram-links`);
      if (!r.ok) {
        if (r.status === 404) return [];
        throw new Error('Failed to load diagram links');
      }
      const j = await r.json();
      return (j.links || []) as DiagramLink[];
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to get all diagrams that link TO this diagram (for "Used In" panel)
 */
export function useReferencingDiagrams(diagramId: string | null) {
  const fetcher = useAuthenticatedFetch();
  const enabled = Boolean(diagramId);

  return useQuery<DiagramLink[]>({
    queryKey: diagramLinksKeys.byTarget(diagramId),
    enabled,
    queryFn: async (): Promise<DiagramLink[]> => {
      if (!diagramId) return [];
      const r = await fetcher(`/api/diagrams/${diagramId}/referenced-by`);
      if (!r.ok) {
        if (r.status === 404) return [];
        throw new Error('Failed to load referencing diagrams');
      }
      const j = await r.json();
      return (j.links || []) as DiagramLink[];
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to get link for a specific shape
 */
export function useShapeLink(shapeId: string | null) {
  const fetcher = useAuthenticatedFetch();
  const enabled = Boolean(shapeId);

  return useQuery<DiagramLink | null>({
    queryKey: diagramLinksKeys.byShape(shapeId),
    enabled,
    queryFn: async (): Promise<DiagramLink | null> => {
      if (!shapeId) return null;
      const r = await fetcher(`/api/shapes/${shapeId}/link`);
      if (!r.ok) {
        if (r.status === 404) return null;
        throw new Error('Failed to load shape link');
      }
      const j = await r.json();
      return (j.link || null) as DiagramLink | null;
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to create a diagram link
 */
export function useCreateDiagramLink() {
  const fetcher = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<DiagramLink, Error, CreateDiagramLinkInput>({
    mutationFn: async (input: CreateDiagramLinkInput): Promise<DiagramLink> => {
      const r = await fetcher(`/api/diagrams/${input.sourceDiagramId}/diagram-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const error = await r.json().catch(() => ({ error: 'Failed to create link' }));
        throw new Error(error.error || 'Failed to create link');
      }
      const j = await r.json();
      return j.link as DiagramLink;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: diagramLinksKeys.byDiagram(data.sourceDiagramId) });
      queryClient.invalidateQueries({ queryKey: diagramLinksKeys.byTarget(data.targetDiagramId) });
      if (data.sourceShapeId) {
        queryClient.invalidateQueries({ queryKey: diagramLinksKeys.byShape(data.sourceShapeId) });
      }
    },
  });
}

/**
 * Hook to delete a diagram link
 */
export function useDeleteDiagramLink() {
  const fetcher = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { linkId: string; sourceDiagramId: string; targetDiagramId: string; sourceShapeId?: string | null }>({
    mutationFn: async ({ linkId }): Promise<void> => {
      const r = await fetcher(`/api/diagram-links/${linkId}`, {
        method: 'DELETE',
      });
      if (!r.ok) {
        const error = await r.json().catch(() => ({ error: 'Failed to delete link' }));
        throw new Error(error.error || 'Failed to delete link');
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: diagramLinksKeys.byDiagram(variables.sourceDiagramId) });
      queryClient.invalidateQueries({ queryKey: diagramLinksKeys.byTarget(variables.targetDiagramId) });
      if (variables.sourceShapeId) {
        queryClient.invalidateQueries({ queryKey: diagramLinksKeys.byShape(variables.sourceShapeId) });
      }
    },
  });
}
